import { Check, CheckCircle, X, XCircle } from 'lucide-react';

import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { VisualBlock } from '@/components/core/visual';
import type { Question } from '@/data/fixtures';
import { cn } from '@/lib/utils';
import {
  ANSWER_BASE,
  ANSWER_TONE,
  MARKER_TONE,
  answerState,
  type AnswerState,
} from './answerTone';

interface QuestionRendererProps {
  question: Question;
  selectedAnswer: string | number | number[] | null;
  showExplanation: boolean;
  onAnswerSelect: (answer: string | number | number[]) => void;
}

/** The verdict glyph after submit: a check for right, a cross for wrong. */
function VerdictIcon({ state }: { state: AnswerState }) {
  if (state === 'correct' || state === 'missed') {
    return <CheckCircle className='h-5 w-5 shrink-0 text-practice' />;
  }
  if (state === 'wrong') {
    return <XCircle className='h-5 w-5 shrink-0 text-destructive' />;
  }
  return null;
}

interface OptionButtonProps {
  state: AnswerState;
  revealed: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  /** Multiple select draws a checkbox before the label. */
  checkbox?: boolean;
  /** "A", "B"... - a letter to scan and refer to. Omitted: no marker. */
  marker?: string;
  /** The option as written; names the button when it is maths, which KaTeX
   *  hides from the accessibility tree (a "$\tfrac12$" button had no name). */
  text?: string;
}

/** A maths option read aloud: "$\tfrac12$" -> "1/2", "$x \ge -2$" -> "x ≥ -2". */
export function spokenOption(text: string): string {
  return text
    .replace(/\\[td]?frac\{([^}]*)\}\{([^}]*)\}/g, '$1/$2')
    .replace(/\\[td]?frac(\d)(\d)/g, '$1/$2')
    .replace(/\^\\circ/g, '°')
    .replace(/\\ge(q)?\b/g, '≥')
    .replace(/\\le(q)?\b/g, '≤')
    .replace(/\\ne(q)?\b/g, '≠')
    .replace(/\\pm\b/g, '±')
    .replace(/\\times\b/g, '×')
    .replace(/\\[a-zA-Z]+/g, '')
    .replace(/[${}]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** One answer option, identical across every option-based question type. */
function OptionButton({
  state,
  revealed,
  onSelect,
  children,
  checkbox = false,
  marker,
  text,
}: OptionButtonProps) {
  const spoken = text?.includes('$') ? spokenOption(text) : undefined;
  return (
    <button
      aria-label={
        spoken ? [marker, spoken].filter(Boolean).join('. ') : undefined
      }
      onClick={() => !revealed && onSelect()}
      disabled={revealed}
      className={cn(ANSWER_BASE, ANSWER_TONE[state])}
    >
      <div className='flex items-center gap-3'>
        {checkbox && <Checkbox state={state} />}
        {marker && <Marker letter={marker} state={state} />}
        {revealed && !marker && <VerdictIcon state={state} />}
        <span className='min-w-0 flex-1'>{children}</span>
        {state === 'missed' && (
          <span className='shrink-0 font-mono text-xs text-practice-emphasis'>
            Missed
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * The option's letter in a circle. After submit it becomes the verdict - a
 * check or a cross - so the letter and the result share one place.
 */
function Marker({ letter, state }: { letter: string; state: AnswerState }) {
  const verdict =
    state === 'correct' || state === 'missed' ? (
      <Check className='h-3.5 w-3.5' />
    ) : state === 'wrong' ? (
      <X className='h-3.5 w-3.5' />
    ) : null;
  return (
    <span
      aria-hidden
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-xs font-semibold transition-colors',
        MARKER_TONE[state]
      )}
    >
      {verdict ?? letter}
    </span>
  );
}

const CHECKBOX_TONE: Record<AnswerState, string> = {
  idle: 'border-border',
  selected: 'border-brand-600 bg-brand-600 dark:border-primary dark:bg-primary',
  correct: 'border-practice bg-practice',
  wrong: 'border-destructive bg-destructive',
  missed: 'border-dashed border-practice',
  neutral: 'border-border',
};

function Checkbox({ state }: { state: AnswerState }) {
  const ticked =
    state === 'selected' || state === 'correct' || state === 'wrong';
  return (
    <div
      aria-hidden
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 transition-colors',
        CHECKBOX_TONE[state]
      )}
    >
      {ticked && <Check className='h-3 w-3 text-white dark:text-card' />}
    </div>
  );
}

function isFillBlankRight(question: Question, answer: string): boolean {
  const accepted = question.acceptedAnswers || [
    question.correctAnswer as string,
  ];
  if (question.caseSensitive === false) {
    return accepted.some(
      a => (a as string).toLowerCase() === answer.toLowerCase()
    );
  }
  return accepted.includes(answer);
}

/** What each question type's renderer needs. */
interface TypeProps {
  question: Question;
  selectedAnswer: QuestionRendererProps['selectedAnswer'];
  revealed: boolean;
  onAnswerSelect: QuestionRendererProps['onAnswerSelect'];
}

/** Options this short ("Part 3", "True") sit two to a row: under a figure,
 *  four stacked options ran below the Submit bar. */
const SHORT_OPTION = 14;

export function optionsGrid(options: string[] | undefined): boolean {
  return Boolean(
    options &&
      options.length > 2 &&
      options.every(o => o.length <= SHORT_OPTION)
  );
}

function MultipleChoice({
  question,
  selectedAnswer,
  revealed,
  onAnswerSelect,
}: TypeProps) {
  return (
    <div
      className={
        optionsGrid(question.options) ? 'grid grid-cols-2 gap-3' : 'space-y-3'
      }
    >
      {question.options?.map((option, index) => (
        <OptionButton
          key={index}
          text={option}
          marker={String.fromCharCode(65 + index)}
          revealed={revealed}
          onSelect={() => onAnswerSelect(index)}
          state={answerState({
            revealed,
            picked: selectedAnswer === index,
            right: index === question.correctAnswer,
          })}
        >
          <InlineMarkdown>{option}</InlineMarkdown>
        </OptionButton>
      ))}
    </div>
  );
}

function MultipleSelect({
  question,
  selectedAnswer,
  revealed,
  onAnswerSelect,
}: TypeProps) {
  const correct = question.correctAnswer as number[];
  const picked = Array.isArray(selectedAnswer) ? selectedAnswer : [];
  const toggle = (index: number) =>
    onAnswerSelect(
      picked.includes(index)
        ? picked.filter(i => i !== index)
        : [...picked, index]
    );
  return (
    <div className='space-y-3'>
      <p className='mb-2 text-xs text-muted-foreground'>
        Select all that apply
      </p>
      {question.options?.map((option, index) => (
        <OptionButton
          key={index}
          text={option}
          checkbox
          revealed={revealed}
          onSelect={() => toggle(index)}
          state={answerState({
            revealed,
            picked: picked.includes(index),
            right: correct.includes(index),
            multiple: true,
          })}
        >
          <InlineMarkdown>{option}</InlineMarkdown>
        </OptionButton>
      ))}
    </div>
  );
}

function TrueFalse({
  question,
  selectedAnswer,
  revealed,
  onAnswerSelect,
}: TypeProps) {
  return (
    <div className='space-y-3'>
      {['true', 'false'].map(option => (
        <OptionButton
          key={option}
          marker={option === 'true' ? 'T' : 'F'}
          revealed={revealed}
          onSelect={() => onAnswerSelect(option)}
          state={answerState({
            revealed,
            picked: selectedAnswer === option,
            right: option === question.correctAnswer,
          })}
        >
          <span className='font-medium capitalize'>{option}</span>
        </OptionButton>
      ))}
    </div>
  );
}

function FillBlank({
  question,
  selectedAnswer,
  revealed,
  onAnswerSelect,
}: TypeProps) {
  const userAnswer = (selectedAnswer as string) || '';
  const right = revealed && isFillBlankRight(question, userAnswer);
  const state: AnswerState = revealed ? (right ? 'correct' : 'wrong') : 'idle';
  return (
    <div className='space-y-3'>
      <div className='relative'>
        <input
          type='text'
          value={userAnswer}
          onChange={e => !revealed && onAnswerSelect(e.target.value)}
          disabled={revealed}
          placeholder={question.placeholder || 'Type your answer...'}
          className={cn(
            ANSWER_BASE,
            ANSWER_TONE[state],
            // Card fill only while typing: after submit the verdict tint
            // must win, and a later `bg-*` would override it.
            !revealed && 'bg-card',
            'outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:focus:border-primary dark:focus:ring-primary/20',
            revealed && 'pr-11'
          )}
        />
        {revealed && (
          <span className='absolute right-4 top-1/2 -translate-y-1/2'>
            <VerdictIcon state={state} />
          </span>
        )}
      </div>
      {revealed && !right && (
        <div className='text-sm text-muted-foreground'>
          <span className='font-medium'>Correct answer:</span>{' '}
          <InlineMarkdown>{question.correctAnswer as string}</InlineMarkdown>
        </div>
      )}
    </div>
  );
}

const RENDERERS: Record<Question['type'], (props: TypeProps) => JSX.Element> = {
  'multiple-choice': MultipleChoice,
  'multiple-select': MultipleSelect,
  'true-false': TrueFalse,
  'fill-blank': FillBlank,
};

export function QuestionRenderer({
  question,
  selectedAnswer,
  showExplanation,
  onAnswerSelect,
}: QuestionRendererProps) {
  const Renderer = RENDERERS[question.type];
  if (!Renderer) return null;
  const options = (
    <Renderer
      question={question}
      selectedAnswer={selectedAnswer}
      revealed={showExplanation}
      onAnswerSelect={onAnswerSelect}
    />
  );
  if (!question.visual) return options;
  // The figure the question asks about sits between the stem and the options.
  return (
    <div className='space-y-4'>
      {/* Compact and captionless: the options must stay in view under the
          figure, and a caption describing the parts can give the answer. */}
      <VisualBlock visual={question.visual} compact caption={false} />
      {options}
    </div>
  );
}
